import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { SkeletonLines } from '../../../components/Skeleton';
import { RESULT_TONES } from '../../../components/status-tones';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { useNow } from '../../../lib/use-now';
import { useCanView } from '../../../stores/session';
import { useMarkingQueue } from '../api/exam-sessions-api';
import { AssessmentsHeader } from '../components/AssessmentsHeader';
import styles from '../components/Assessments.module.css';

const HOUR_MS = 3_600_000;
/** §L22 N-25: marking is overdue at three days. */
const OVERDUE_HOURS = 72;

/**
 * ASM-06 — written answers waiting for a person (F9.07), oldest first. A grader sees their
 * own; the HR Lead sees everyone's, with who each is waiting on. How long each has waited
 * is shown because it is counted (E7: time-to-grade per grader).
 */
export function MarkingQueuePage(): ReactNode {
  const canView = useCanView('assessment_attempt');
  const queue = useMarkingQueue();
  const now = useNow();

  if (!canView) return <ForbiddenState resource="marking" contact="Your admin" />;

  return (
    <div className={styles.screen}>
      <AssessmentsHeader current="marking" />
      <Card title="Marking" meta="Written answers waiting for a person. Nobody moves on until theirs are marked">
        {queue.isPending ? (
          <SkeletonLines count={4} />
        ) : queue.error ? (
          <ServerErrorState onRetry={() => void queue.refetch()} />
        ) : queue.data.length === 0 ? (
          <EmptyState isInset title="Nothing to mark" body="Tests with written answers appear here as candidates submit them." action={null} />
        ) : (
          <ul className={styles.list}>
            {queue.data.map((row) => {
              const hours = (now - new Date(row.submittedAt).getTime()) / HOUR_MS;
              return (
                <li key={row.attemptId} className={styles.item}>
                  <div className={styles.itemHead}>
                    <Link to="/assessments/marking/$attemptId" params={{ attemptId: row.attemptId }} className={styles.itemName}>
                      {row.candidateName}
                    </Link>
                    <Chip tone={hours >= OVERDUE_HOURS ? 'urgent' : RESULT_TONES.marking} plain>
                      {row.answersToMark} to mark
                    </Chip>
                    <span className={styles.meta} title={formatDateTime(row.submittedAt)}>
                      submitted {formatRelative(row.submittedAt)}
                    </span>
                  </div>
                  <p className={styles.route}>
                    {row.testName} · {row.jobTitle}
                    {row.graderName ? ` · marker ${row.graderName}` : ''}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
